import { isiUnit, isReportId, reportById, uiStrings } from '@satu/shared'
import { createBrowserRouter, type RouteObject, redirect, type UIMatch } from 'react-router'
import { AuthGuard } from '../auth/guard'
import { JagaDataParoki, JagaPeta } from '../routes/hanyaParoki'
import {
  BuatPinLazy,
  DuaLangkahLazy,
  KonfirmasiEmailLazy,
  LupaPinLazy,
  LupaSandiLazy,
  MasukLayout,
  MasukLazy,
  PinLazy,
  SandiAwalLazy,
  SandiBaruLazy,
  UnduhanPertamaLazy,
} from '../routes/masuk'
import { HalamanGagal } from './routes/HalamanGagal'
import { HalamanSementara, TidakDitemukan } from './routes/sementara'
import { rutePelayan } from './rutePelayan'
import { AppShell } from './shell/AppShell'
import { NAV_GROUPS, navItems } from './shell/nav'
import { sebutanKini } from './store/sebutanUnit'

// Data mode (createBrowserRouter): useMatches for breadcrumbs, and navigations already run
// inside React.startTransition so the shell's <ViewTransition> animates every route change.
// Every route of the bookshelf exists here with stand-in content; sections 06 onward swap in
// their screens (and lazy chunks) route by route. /masuk and its steps render outside the
// shell; the AuthGuard (section 05) in front of the shell sends anyone without a session or
// with the PIN lock on to them. Routes whose screens exist are dropped from the stand-ins and
// listed below.
const dibangun = new Set([
  '/verifikasi',
  '/sinkron',
  '/keluarga',
  '/umat',
  '/mutasi',
  '/pendaftaran',
  '/impor',
  '/surat',
  '/laporan',
  '/buku/baptis',
  '/buku/komuni',
  '/buku/krisma',
  '/buku/perkawinan',
  '/buku/kematian',
  '/admin',
  '/pelayan',
  '/peta',
])
const halaman = navItems(NAV_GROUPS)
  .filter((item) => item.to !== '/' && !dibangun.has(item.to))
  .map((item) => ({
    path: item.to,
    element: <HalamanSementara judul={item.label} />,
    handle: { crumb: item.label },
  }))

// Section 08: /keluarga is a layout route (the list, kept mounted under Activity) with the
// card as its child; /umat/:id is the person record. One lazy chunk for the family.
const keluarga = {
  path: '/keluarga',
  lazy: async () => ({ Component: (await import('../routes/keluarga')).KeluargaLayout }),
  handle: { crumb: 'Keluarga' },
  children: [
    {
      path: ':id',
      lazy: async () => ({ Component: (await import('../routes/keluarga')).KartuKeluarga }),
      handle: { crumb: 'Kartu keluarga' },
    },
  ],
}
// Daftar Umat (spec 2026-09-30): every person in scope; its own chunk.
const daftarUmat = {
  path: '/umat',
  lazy: async () => ({
    Component: (await import('../routes/umat/daftar/DaftarUmatRoute')).DaftarUmatRoute,
  }),
  handle: { crumb: 'Umat' },
}
// Tambah umat (spec 2026-09-30, section 6): the step-by-step wizard, its own chunk. Listed
// before /umat/:id; the static "baru" also outranks the id segment in react-router's ranking.
const tambahUmat = {
  path: '/umat/baru/:umatId?/:langkah?',
  lazy: async () => ({
    Component: (await import('../routes/umat/baru/TambahUmatRoute')).TambahUmatRoute,
  }),
  handle: { crumb: uiStrings.tambahUmat.judul },
}
const umat = {
  path: '/umat/:id',
  lazy: async () => ({ Component: (await import('../routes/umat/UmatRoute')).UmatRoute }),
  handle: { crumb: 'Umat' },
}

// Section 09: the desk registration flow, one chunk. The family and the step are optional
// segments so /pendaftaran (a new family), /pendaftaran/:id (resume at the first open step)
// and /pendaftaran/:id/:langkah share one screen; `?keluarga=&langkah=` redirect into it.
const pendaftaran = {
  path: '/pendaftaran/:keluargaId?/:langkah?',
  lazy: async () => ({ Component: (await import('../routes/pendaftaran')).PendaftaranRoute }),
  handle: { crumb: 'Pendaftaran' },
}

// Section 10: the five register books share one chunk. `/buku/:jenis` is a layout route
// (its crumb names the book) with the book screen as index, the ceremony flow as `upacara`
// (Tambah entri of Baptis, Komuni Pertama and Krisma) and the entry view as `:id`; the copying
// mode is a flat route so nothing but its band sits at the top.
const JUDUL_BUKU_SLUG: Record<string, string> = {
  baptis: uiStrings.buku.judul.baptis,
  komuni: uiStrings.buku.judul.komuni_pertama,
  krisma: uiStrings.buku.judul.krisma,
  perkawinan: uiStrings.buku.judul.perkawinan,
  kematian: uiStrings.buku.judul.kematian,
}
const buku: RouteObject = {
  path: '/buku/:jenis',
  lazy: async () => ({ Component: (await import('../routes/buku')).BukuLayout }),
  handle: {
    crumb: (match: UIMatch) => JUDUL_BUKU_SLUG[String(match.params.jenis)] ?? '',
  },
  children: [
    {
      index: true,
      lazy: async () => ({ Component: (await import('../routes/buku')).BukuRoute }),
    },
    {
      // Release 32: a Pastor Paroki reads the book and adds no entry.
      element: <JagaDataParoki ubah />,
      children: [
        {
          // Tambah entri of Baptis, Komuni Pertama and Krisma (the ceremony flow, spec
          // 2026-10-02; owner 2026-10-04): a chunk of its own, inside the book layout (no ketua).
          path: 'upacara',
          lazy: async () => ({
            Component: (await import('../routes/upacara/UpacaraRoute')).UpacaraRoute,
          }),
          handle: { crumb: uiStrings.upacara.judul },
        },
      ],
    },
    {
      path: ':id',
      lazy: async () => ({ Component: (await import('../routes/buku')).EntriRoute }),
      handle: { crumb: uiStrings.buku.entri.remah },
    },
  ],
}
const salinBuku = {
  path: '/buku/:jenis/salin',
  lazy: async () => ({ Component: (await import('../routes/buku')).SalinBukuRoute }),
  // `salin` lets the shell step aside for Meja Bersih (AppShell).
  handle: { crumb: uiStrings.buku.modeSalinBuku, salin: true },
}

// Section 12: parish transfers. One lazy chunk: the list, the form and the request page
// under a layout whose crumb is "Mutasi".
const mutasi = {
  path: '/mutasi',
  lazy: async () => ({ Component: (await import('../routes/mutasi')).MutasiLayout }),
  handle: { crumb: uiStrings.mutasi.judul },
  children: [
    {
      index: true,
      lazy: async () => ({ Component: (await import('../routes/mutasi')).MutasiRoute }),
    },
    {
      path: 'ajukan',
      lazy: async () => ({ Component: (await import('../routes/mutasi')).AjukanMutasi }),
      handle: { crumb: uiStrings.mutasi.ajukan },
    },
    {
      path: ':id',
      lazy: async () => ({ Component: (await import('../routes/mutasi')).MutasiDetail }),
      handle: { crumb: uiStrings.mutasi.detail.remah },
    },
  ],
}

// Section 11: the issued letters (`/surat`, `/surat/:id`) and the print route
// (`/cetak/:jenis/:id?`, the sheet at physical size; other sections register their
// templates in `print/registry.ts`). Each screen is its own chunk; a template loads only
// when asked for.
const surat = {
  path: '/surat',
  lazy: async () => ({ Component: (await import('../routes/surat')).SuratRoute }),
  handle: { crumb: uiStrings.surat.judul },
}
const suratDetail = {
  path: '/surat/:id',
  lazy: async () => ({ Component: (await import('../routes/surat')).SuratDetailRoute }),
  handle: { crumb: uiStrings.surat.detail.remah },
}
const cetak = {
  path: '/cetak/:jenis/:id?',
  lazy: async () => ({ Component: (await import('../routes/cetak/CetakRoute')).CetakRoute }),
  handle: { crumb: uiStrings.surat.cetakRute.remah },
}

// Section 14: the report shelf, an opened report, the Kelengkapan table and its drill-down,
// one lazy chunk under a layout whose crumb is "Laporan"; section 15's Statistik Tahunan
// rides in the same chunk.
const laporan = {
  path: '/laporan',
  lazy: async () => ({ Component: (await import('../routes/laporan')).LaporanLayout }),
  handle: { crumb: uiStrings.laporan.judul },
  children: [
    {
      index: true,
      lazy: async () => ({ Component: (await import('../routes/laporan')).LaporanRoute }),
    },
    {
      path: 'statistik-tahunan',
      lazy: async () => ({
        Component: (await import('../routes/laporan')).StatistikTahunanRoute,
      }),
      handle: { crumb: uiStrings.laporan.kartu.statistik_tahunan },
    },
    {
      path: 'kelengkapan',
      lazy: async () => ({ Component: (await import('../routes/laporan')).KelengkapanRoute }),
      handle: { crumb: uiStrings.laporan.kartu.kelengkapan_data },
    },
    {
      path: 'kelengkapan/:lingkunganId',
      lazy: async () => ({ Component: (await import('../routes/laporan')).KelengkapanDetail }),
      handle: { crumb: uiStrings.laporan.kelengkapan.remah },
    },
    {
      path: ':id',
      lazy: async () => ({ Component: (await import('../routes/laporan')).LaporanTableRoute }),
      handle: {
        crumb: (match: UIMatch) => {
          const id = String(match.params.id)
          // "Umat per kombas" in a parish that says Kombas (Sebutan unit terkecil).
          return isReportId(id) ? isiUnit(reportById(id).judul, sebutanKini()) : ''
        },
      },
    },
  ],
}

// Section 18 and plan 9b: the parish settings, Admin Paroki only (any other role is taken to
// Akun); the parish form loads in its own chunk inside it.
const pengaturan = {
  path: '/pengaturan',
  lazy: async () => ({ Component: (await import('../routes/pengaturan')).PengaturanRoute }),
  handle: { crumb: uiStrings.pengaturan.judul },
}

// Peta umat (release 20): the parish roles and both ketua in their own scope, the admin
// keuskupan's diocese view with a parish picker; its own chunk, Leaflet loaded inside it (never
// with the launch). Ruling 2: the uskup and a Ketua Wilayah have no Peta: the guard says so in one
// sentence before the screen's chunk loads.
const peta = {
  element: <JagaPeta />,
  children: [
    {
      path: '/peta',
      lazy: async () => ({ Component: (await import('../routes/peta/PetaRoute')).PetaRoute }),
      handle: { crumb: uiStrings.peta.judul },
    },
  ],
}

// Akun & keamanan (spec 2026-09-30): the own account, every role, its own chunk.
const akun = {
  path: '/akun',
  lazy: async () => ({ Component: (await import('../routes/akun/AkunRoute')).AkunRoute }),
  handle: { crumb: uiStrings.akun.judul },
}

// Administrasi (spec 2026-09-30-administrasi, 4.1): a layout with four sub-pages, each its
// own chunk; `/admin` opens Wilayah. Only an admin keuskupan sees past the layout. The
// redirect is a loader's, so `/admin` itself never renders: a <Navigate> showed the title
// and tabs without the breadcrumb for a moment, then crossfaded again and jumped down.
const admin = {
  path: '/admin',
  lazy: async () => ({ Component: (await import('../routes/admin/AdminLayout')).AdminLayout }),
  handle: { crumb: uiStrings.admin.judul },
  children: [
    { index: true, loader: () => redirect('/admin/wilayah') },
    {
      path: 'wilayah',
      lazy: async () => ({
        Component: (await import('../routes/admin/WilayahRoute')).WilayahRoute,
      }),
      handle: { crumb: uiStrings.admin.bagian.wilayah },
    },
    {
      path: 'pengguna',
      lazy: async () => ({
        Component: (await import('../routes/admin/PenggunaRoute')).PenggunaRoute,
      }),
      handle: { crumb: uiStrings.admin.bagian.pengguna },
    },
    {
      path: 'perangkat',
      lazy: async () => ({
        Component: (await import('../routes/admin/PerangkatRoute')).PerangkatRoute,
      }),
      handle: { crumb: uiStrings.admin.bagian.perangkat },
    },
    {
      path: 'status',
      lazy: async () => ({ Component: (await import('../routes/admin/StatusRoute')).StatusRoute }),
      handle: { crumb: uiStrings.admin.bagian.statusLayanan },
    },
    {
      // Paroki baru (spec 2026-09-30-administrasi 4.3): its own chunk, without the tabs.
      path: 'paroki-baru',
      lazy: async () => ({
        Component: (await import('../routes/admin/ParokiBaruRoute')).ParokiBaruRoute,
      }),
      handle: { crumb: uiStrings.admin.parokiBaru.judul, tanpaBingkai: true },
    },
  ],
}

// Section 17: the CSV import, its own chunk (PapaParse loads on demand inside it).
const impor = {
  path: '/impor',
  lazy: async () => ({
    Component: (await import('../routes/impor/ImporRoute')).ImporRoute,
  }),
  handle: { crumb: 'Impor' },
}

// Beranda (charts aside) is a lazy chunk too: it is never on screen before the PIN or the
// first download, so it loads 2.5 s after launch instead of competing with the first paint.
const muatBeranda = () => import('@/routes/beranda/BerandaRoute')
if (typeof window !== 'undefined') window.setTimeout(() => void muatBeranda(), 2500)

// The access ruling of 2026-10-03 (plan 10): the parish's data screens serve the parish roles
// and an admin keuskupan working in a parish; a diocese view reads one sentence. Mutasi and
// Impor serve the parish staff only. Pathless layouts: addresses and crumbs stay as they are.
// Release 32: the screens that only change the parish's data (Tambah umat, Pendaftaran, Salin
// buku) are refused to a Pastor Paroki, who reads the rest.
const dataParoki = {
  element: <JagaDataParoki />,
  children: [
    keluarga,
    daftarUmat,
    umat,
    buku,
    surat,
    suratDetail,
    { element: <JagaDataParoki ubah />, children: [tambahUmat, pendaftaran, salinBuku] },
  ],
}
const dataStafParoki = { element: <JagaDataParoki tanpaKetua />, children: [mutasi, impor] }

export const router = createBrowserRouter([
  {
    path: '/masuk',
    element: <MasukLayout />,
    children: [
      { index: true, element: <MasukLazy /> },
      { path: 'pin', element: <PinLazy /> },
      { path: 'buat-pin', element: <BuatPinLazy /> },
      { path: 'lupa-pin', element: <LupaPinLazy /> },
      // Akun & keamanan (spec 2026-09-30): the steps after the password, the forgotten
      // password, the reset link's page and the e-mail change's confirmation (no sign-in).
      { path: 'sandi-awal', element: <SandiAwalLazy /> },
      { path: 'dua-langkah', element: <DuaLangkahLazy /> },
      { path: 'lupa-sandi', element: <LupaSandiLazy /> },
      { path: 'sandi-baru', element: <SandiBaruLazy /> },
      { path: 'konfirmasi-email', element: <KonfirmasiEmailLazy /> },
      { path: 'unduhan', element: <UnduhanPertamaLazy /> },
    ],
  },
  {
    element: <AuthGuard />,
    children: [
      {
        id: 'kerangka',
        element: <AppShell />,
        children: [
          {
            // A screen that fails to open (its chunk unreachable offline, finding C8) reads
            // one Indonesian sentence inside the shell, never the router's own error page.
            errorElement: <HalamanGagal />,
            children: [
              {
                index: true,
                lazy: async () => ({ Component: (await muatBeranda()).BerandaRoute }),
                handle: { crumb: 'Beranda' },
              },
              ...halaman,
              {
                path: '/verifikasi',
                lazy: async () => ({
                  Component: (await import('../routes/verifikasi/VerifikasiRoute')).VerifikasiRoute,
                }),
                handle: { crumb: 'Verifikasi' },
              },
              {
                // Section 16: the sync ledger, its own chunk (NumberFlow rides along).
                path: '/sinkron',
                lazy: async () => ({
                  Component: (await import('../routes/sinkron/SinkronRoute')).SinkronRoute,
                }),
                handle: { crumb: 'Sinkronisasi' },
              },
              dataParoki,
              dataStafParoki,
              cetak,
              laporan,
              peta,
              pengaturan,
              akun,
              rutePelayan,
              admin,
              { path: '*', element: <TidakDitemukan /> },
            ],
          },
        ],
      },
    ],
  },
])
